/**
 * utils/piutangUtang.js
 * Pure helpers backing the "Piutang & Utang" page — splitting outstanding (non-Lunas)
 * transactions into Piutang (real customers who owe), Hutang (real suppliers owed), and
 * Internal (outstanding Transfers, one row per transfer — Piutang & Utang batch 3; see
 * `splitOutstanding`'s own comment for why a Transfer no longer lands in Piutang), and naming a
 * Transfer's real debtor for display.
 *
 * `transferStatusFor` itself now lives in `utils/txnStatus.js` (moved there in the same batch —
 * see that file's own header comment on the function), re-exported from here unchanged so every
 * existing import path keeps working.
 *
 * Also the home of `outstanding`/`outstandingForContact`/`outstandingForBusiness` — the
 * direction-aware outstanding figure for ONE contact or ONE business (KontakPage's Bisnis
 * card). `splitOutstanding` above answers "which transactions, app-wide" (a list of
 * transactions per bucket); these answer "how much, for one contact/business" (three numbers:
 * utang, piutang, nett), which `splitOutstanding` was never asked to do and doesn't do — a
 * contact that both buys and sells has both a real Utang and a real Piutang at once, and
 * summing `txnSisa` across all of its transactions (the old `KontakPage.js` `sumSisa` helper)
 * blends the two into one number that means nothing specific (not "how much they owe us," not
 * "how much we owe them," not really either). See each function's own comment below.
 */
import { txnStatus, txnSisa, transferStatusFor } from "./txnStatus";
import { resolveBranchName, resolveLocationLabel } from "./branches";

// Re-exported so every existing `import { transferStatusFor } from "../utils/piutangUtang"`
// (its own 6 dedicated tests included) keeps resolving — Piutang & Utang batch 3 moved its real
// implementation into `txnStatus.js` (see that file's own header comment on this function for
// why: single-transaction math, and the circular-import this would otherwise create once
// `txnStatus()` itself needs to call it). This file's own `outstanding()` below now imports it
// from there too, like any other consumer — there is exactly one implementation.
export { transferStatusFor };

/**
 * Splits `transactions` into { piutang, hutang, internal }, dropping every LUNAS transaction
 * entirely — this drop is what makes a transaction disappear from the page the instant it's
 * paid off.
 *
 * **Piutang & Utang batch 3 — TRANSFER moved OUT of `piutang` into its own `internal` bucket.**
 * A transfer is not a real customer/supplier debt in either direction; it's the same business
 * moving stock between its own two bookkeeping sides. Folding it into `piutang` (the old,
 * whole-business-view behavior) meant a transfer that's outstanding showed up looking exactly
 * like a real customer owing money, with no way to tell the two apart on this page. `piutang`/
 * `hutang` now reuse `txnStatus`'s own PENJUALAN-vs-PEMBELIAN branching exactly as before, but
 * only ever see non-TRANSFER transactions; every outstanding (non-zero-`txnSisa`) TRANSFER lands
 * in `internal` instead, one row per transfer — never split into two rows for two directions,
 * since a transfer is one movement, not a debt in two directions, from the whole business's own
 * chair. `txnSisa(t) !== 0` (not `txnStatus`) is what decides TRANSFER inclusion, since
 * `txnStatus` without a perspective argument still answers the old whole-business "PIUTANG" for
 * a TRANSFER — a question this function deliberately no longer asks for that type.
 */
export function splitOutstanding(transactions) {
  const piutang = [];
  const hutang = [];
  const internal = [];
  for (const t of transactions || []) {
    if (t.type === "TRANSFER") {
      if (txnSisa(t) !== 0) internal.push(t);
      continue;
    }
    const status = txnStatus(t);
    if (status === "PIUTANG") piutang.push(t);
    else if (status === "UTANG") hutang.push(t);
  }
  return { piutang, hutang, internal };
}

/**
 * Direction-aware outstanding for ONE already-filtered list of transactions — the core these
 * three functions build on. Buckets each non-Lunas transaction's `txnSisa` by `txnStatus`'s own
 * PIUTANG/UTANG classification (never re-derives `t.type` itself, same "reuse the one real
 * classification" discipline `splitOutstanding` above already follows) — so this can never
 * disagree with `splitOutstanding`/`txnStatus` about which side a transaction falls on.
 *
 * `perspectiveBranchId` (optional, same shape as `transferStatusFor`'s own — `null` for Pusat, a
 * real branchId for that Cabang branch) is what lets a TRANSFER be classified for ONE side's
 * books instead of throwing (see the TRANSFER section below). Omit it entirely to keep today's
 * exact behavior on a TRANSFER-bearing list.
 *
 * Returns `{ utang, piutang, nett, nettArah, internal }`:
 *   - `utang`/`piutang` are each always >= 0 (a plain sum of non-negative `txnSisa` values).
 *   - `nett = piutang - utang` (signed; positive means piutang).
 *   - `nettArah` is `"PIUTANG"` (nett > 0), `"UTANG"` (nett < 0), or `"NOL"` (nett === 0) —
 *     computed HERE so no caller re-derives the sign-to-label mapping itself.
 *   - `internal.utang`/`internal.piutang`: how much of each bucket's total came from a TRANSFER
 *     specifically (as opposed to an ordinary PENJUALAN/PEMBELIAN) — real, non-zero values once
 *     `perspectiveBranchId` is supplied and a Belum Lunas TRANSFER is actually present; `{0, 0}`
 *     whenever no TRANSFER contributed (including every call that omits `perspectiveBranchId` and
 *     hits no TRANSFER at all). See the TRANSFER section below for why this could ONLY ever be
 *     `{0, 0}` before `perspectiveBranchId` existed, and never leave it at `{0, 0}` as a stand-in
 *     for "not computed" now that it genuinely can be — a zero here must mean "no TRANSFER in this
 *     bucket," never "TRANSFER classification wasn't attempted," for the same `fmtQty` reason the
 *     TRANSFER guard below already treats a sentinel number as unsafe.
 *
 * **TRANSFER — a real guard, not a comment reusing someone else's rule.** An earlier version of
 * this function reused `txnStatus`'s own existing, app-wide rule for a TRANSFER's direction
 * (always PIUTANG — "the same business on both ends," a true statement at the WHOLE-BUSINESS
 * level) and only WARNED in a comment that this might not be the right answer for ONE specific
 * contact/branch. A comment doesn't stop a caller from reading `.nettArah`/`.nett` and getting a
 * confident-looking number this function cannot actually back up per-contact.
 *   - **When `perspectiveBranchId` is supplied**: a Belum Lunas TRANSFER is classified via
 *     `transferStatusFor(t, perspectiveBranchId)` — a real PIUTANG/UTANG answer for THAT side's
 *     books, added to the matching bucket (and mirrored into `internal.utang`/`internal.piutang`)
 *     same as any other transaction. `transferStatusFor`'s own throws (wrong branch, etc.)
 *     propagate unchanged — this function adds no extra guessing on top.
 *   - **When `perspectiveBranchId` is omitted (`undefined`) — the original, unconditional
 *     behavior, byte-for-byte**: `outstanding()` THROWS instead of returning anything when it
 *     encounters a Belum Lunas (real, non-zero `txnSisa`) TRANSFER row — deciding a per-branch
 *     direction with no side named is exactly the guess this guard was built to refuse. A LUNAS
 *     TRANSFER never triggers it either way (see below) — there is nothing outstanding to
 *     misclassify in that case.
 *   - **Why throw, not a sentinel value, when no perspective is given**: every numeric field here
 *     is eventually rendered through `fmtQty` (`utils/idGenerators.js`), which formats
 *     `null`/`undefined`/`NaN` IDENTICALLY to a real `0` — "Rp 0" either way, on purpose, for
 *     genuinely-unset display fields elsewhere in this app. A sentinel placed in
 *     `utang`/`piutang`/`nett` (or a `nettArah` value like `"TIDAK_DIKETAHUI"` sitting next to
 *     real-looking numbers) could still be misread as a confident answer by a caller that forgot
 *     to check a flag first — the exact failure this guard exists to close. A thrown error is the
 *     one mechanism nothing can silently render as a number.
 *   - **This is the one function in `src/utils` that throws.** Every other pure function here
 *     answers an unresolvable/invalid input with a same-reference no-op, `null`, or an
 *     `{ok,error}` shape (e.g. `resolveBranchName`, `validateContactFields`) — but those all
 *     guard USER input a form is expected to have already validated. This guards a CALLER's own
 *     precondition ("don't ask this function to classify a TRANSFER's direction without naming a
 *     side") — a programmer-facing precondition, not a data-entry one, closer to an assertion
 *     than a validation, which is why a different mechanism fits here.
 *   - **`outstandingForContact`/`outstandingForBusiness` back KontakPage's Bisnis card**, which
 *     never renders for an internal contact (`!selected.internal`), and no `Business` can ever
 *     link one (`utils/contacts.js`'s `makeInternalContact` hardcodes `businessId: null`,
 *     `updateContact` refuses every write to an internal contact outright, and
 *     `BusinessLinkModal.js`'s own picker filters internal contacts out of what it offers). A
 *     TRANSFER's own `contactId` is always an internal contact by construction (`App.js`'s
 *     `createTransaction` resolves it via `resolveTransferContact` before it ever reaches a
 *     normal contact's `list` check) — confirmed by direct read, not assumed. So a call through
 *     either wrapper still only ever reaches a TRANSFER via a path this file's own read confirmed
 *     is internal-contact-only; passing `perspectiveBranchId` through them today has no live
 *     caller yet (KontakPage never supplies one), but the throw-when-omitted behavior means
 *     nothing changes for either wrapper's existing callers. **This does NOT mean a normal
 *     (non-internal) contact can never end up with an internal-looking mix by some OTHER path** —
 *     `createTransaction`/`editTransaction`'s own write-path guard for PENJUALAN/PEMBELIAN only
 *     checks `contact.list !== location`, never `contact.internal`, so a picker bug that ever let
 *     an internal contact be picked for an ordinary sale/purchase would go unchallenged at save
 *     time; that's a distinct, currently theoretical gap in the write path, not something this
 *     read-only guard can or should close. This guard is about TRANSFER transactions
 *     specifically, which today can only ever be attached to an internal contact — never about
 *     internal contacts in general.
 *   - **A LUNAS TRANSFER never triggers the throw or reaches `transferStatusFor`** — checked
 *     AFTER the existing Lunas skip (`sisa === 0`), not by scanning `transactions` for
 *     `type === "TRANSFER"` up front. A fully-settled inter-branch transfer contributes zero to
 *     either bucket regardless of direction — there is genuinely nothing to misclassify, so
 *     refusing harmless data (or bothering to resolve a side for it) would be pointless. Only a
 *     Belum Lunas TRANSFER (real, non-zero `txnSisa`) is an actual unresolved-without-a-side
 *     direction.
 */
export function outstanding(transactions, perspectiveBranchId) {
  let utang = 0;
  let piutang = 0;
  let internalUtang = 0;
  let internalPiutang = 0;
  for (const t of transactions || []) {
    const sisa = txnSisa(t);
    if (sisa === 0) continue; // LUNAS (or a genuinely zero-total txn) — nothing outstanding
    if (t.type === "TRANSFER") {
      if (perspectiveBranchId === undefined) {
        throw new Error(
          "outstanding(): cannot classify a Belum Lunas TRANSFER's direction for one " +
            "contact/business — the app-wide rule (always Piutang) is a whole-business " +
            "statement, not a per-contact one. Filter TRANSFER transactions out before " +
            "calling this, or pass a perspectiveBranchId (see transferStatusFor) to resolve " +
            "a real per-branch direction."
        );
      }
      const status = transferStatusFor(t, perspectiveBranchId);
      if (status === "UTANG") {
        utang += sisa;
        internalUtang += sisa;
      } else {
        piutang += sisa;
        internalPiutang += sisa;
      }
      continue;
    }
    const status = txnStatus(t);
    if (status === "UTANG") utang += sisa;
    else if (status === "PIUTANG") piutang += sisa;
  }
  const nett = piutang - utang;
  const nettArah = nett > 0 ? "PIUTANG" : nett < 0 ? "UTANG" : "NOL";
  return { utang, piutang, nett, nettArah, internal: { utang: internalUtang, piutang: internalPiutang } };
}

/**
 * `outstanding`, scoped to one contact's own transactions (`t.contactId === contactId`).
 * **Inherits `outstanding`'s own TRANSFER guard**: if THIS contact's own transactions include
 * any TRANSFER, this throws when `perspectiveBranchId` is omitted — filtered first, so a
 * TRANSFER belonging to some OTHER contact in the same `transactions` array never triggers it.
 * `perspectiveBranchId` (optional) passes straight through to `outstanding` — see that
 * function's and `transferStatusFor`'s own comments for its shape and meaning.
 */
export function outstandingForContact(transactions, contactId, perspectiveBranchId) {
  return outstanding((transactions || []).filter((t) => t.contactId === contactId), perspectiveBranchId);
}

/**
 * `outstanding`, scoped to every contact linked to one business (`c.businessId === businessId`)
 * — the combined firm-wide figure. `contacts` resolves which contactIds belong to the firm;
 * `transactions` is filtered to those ids' transactions, then summed the same way
 * `outstandingForContact` does for one. An internal contact can never carry a `businessId` (see
 * `outstanding`'s own header above), so this never picks one up even if `contacts` contains one.
 * **Inherits `outstanding`'s own TRANSFER guard** the same way — throws when
 * `perspectiveBranchId` is omitted and ANY linked contact's own transactions include a TRANSFER,
 * never if an unlinked contact elsewhere in `transactions` happens to have one.
 * `perspectiveBranchId` (optional) passes straight through to `outstanding`.
 */
export function outstandingForBusiness(transactions, contacts, businessId, perspectiveBranchId) {
  const contactIds = new Set((contacts || []).filter((c) => c.businessId === businessId).map((c) => c.id));
  return outstanding((transactions || []).filter((t) => contactIds.has(t.contactId)), perspectiveBranchId);
}

/**
 * The real debtor's name for a TRANSFER row. A Pusat→Cabang transfer's debtor is the real
 * destination branch (resolved via branchId); a Cabang→Pusat transfer's debtor is Pusat
 * itself, which is never a `branches`-list entry.
 */
export function transferKlienName(txn, branches) {
  if (txn.transferDirection === "PUSAT_TO_CABANG") {
    return resolveBranchName(branches, txn.branchId) || "Cabang";
  }
  return "Pusat";
}

/**
 * "{source} → {destination}" for a TRANSFER row — the exact wording Piutang & Utang's own
 * Internal tab already shows on its Pihak cell's bold line (`PiutangUtang.js`'s
 * `internalPihakCell`), e.g. "Pusat → AMIN". Extracted here (section F, batch 5) so a second
 * screen (`KontakPage.js`'s own Riwayat Transaksi, which shows a mix of transfer directions on
 * one internal contact's page with no other visible clue) can show the IDENTICAL string instead
 * of inventing a second phrasing — one rule, two places. Built from the same two primitives
 * Piutang & Utang already uses for its own two names — `resolveLocationLabel(txn.location,
 * txn.branchId, branches)` (`utils/branches.js`, the source/seller) and `transferKlienName`
 * (above, the destination/buyer/real debtor) — so it can never disagree with either page about
 * WHAT the two names are, only (optionally) how they're styled around this plain string.
 */
export function transferDirectionLabel(txn, branches) {
  return `${resolveLocationLabel(txn.location, txn.branchId, branches)} → ${transferKlienName(txn, branches)}`;
}
